/**
 * Appeals — the staff side of the appeal process (0305).
 *
 * Two kinds land here from /api/me/appeals:
 *
 *   suspension    the account is write-suspended and its owner says that is
 *                 wrong. GRANT UNSUSPENDS in the same transaction.
 *   post_removal  a staff takedown of the author's own post. GRANT RESTORES
 *                 the post and voids the strikes it caused (0178 — reversed
 *                 enforcement must not keep counting toward the ladder).
 *   group_moderation  staff restricted or closed a group chat and one of its
 *                 admins says that is wrong (0411). GRANT LIFTS the group.
 *
 * A granted appeal that changed nothing would be worse than no appeal system
 * at all, so the reversal is applied server-side by the decide endpoint, not
 * by a second manual step someone can forget.
 *
 * The open queue is OLDEST-FIRST: the person who has waited longest is next.
 * Deny requires no note but deserves one — it is shown back to the user on
 * their appeal screen, and "denied, no reason" reads as no process at all.
 * Two-step confirm on both actions (§0c — no window.confirm).
 */

import { parseServerDate } from '@postopia/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { moderationApi, type AccountAppeal } from '../lib/moderationApi';
import { PageShell } from '../components/ui/PageShell';
import { TabBar } from '../components/ui/TabBar';

export const Route = createFileRoute('/appeals')({
  component: AppealsRoute,
});

function AppealsRoute() {
  return (
    <AdminShell mode="feature" featureName="Appeals">
      <AppealsConsole />
    </AdminShell>
  );
}

type View = 'open' | 'decided';

function AppealsConsole() {
  const [view, setView] = useState<View>('open');
  const [items, setItems] = useState<AccountAppeal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [arming, setArming] = useState<{ id: string; action: 'grant' | 'deny' } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    setItems(null);
    setError(null);
    moderationApi
      .appeals(view)
      .then((r) => setItems(r.appeals))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'failed'));
  }, [view]);
  useEffect(load, [load]);

  async function decide(id: string, action: 'grant' | 'deny') {
    if (arming?.id !== id || arming.action !== action) {
      setArming({ id, action });
      return;
    }
    setBusy(id);
    try {
      await moderationApi.appealDecide(id, action, notes[id]?.trim() || undefined);
      setItems((cur) => (cur ? cur.filter((a) => a.id !== id) : cur));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'failed');
    } finally {
      setBusy(null);
      setArming(null);
    }
  }

  return (
    <PageShell
      title="Appeals"
      description="Moderation decisions a user has asked us to look at again. Granting an appeal reverses the original action."
      maxWidth={1100}
    >
      <TabBar
        label="Appeal queue"
        activeKey={view}
        onSelect={setView}
        items={[
          { key: 'open', label: 'Waiting' },
          { key: 'decided', label: 'Decided' },
        ]}
      />

      {error ? (
        <p role="alert" className="mt-3 text-[13px]" style={{ color: 'var(--c-state-danger, #C7563E)' }}>
          {error}
        </p>
      ) : null}

      {/* A failed load shows its error, not an endless "Loading…" (Q audit 2026-09-23). */}
      {error && items === null ? null : items === null ? (
        <p className="mt-4 text-[13px]" style={{ color: 'var(--c-fg-tertiary)' }}>Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-4 text-[13px]" style={{ color: 'var(--c-fg-tertiary)' }}>
          {view === 'open' ? 'No appeals waiting.' : 'No decided appeals yet.'}
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-3">
          {items.map((a) => (
            <li key={a.id} className="rounded-soft border border-hairline bg-surface p-3 text-[13px]" style={{ color: 'var(--c-fg-default)' }}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-semibold">@{a.handle}</span>
                <span
                  className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  style={{
                    background: 'var(--c-bg-surface-alt)',
                    color: a.kind === 'suspension' ? '#9A4230' : 'var(--c-fg-secondary)',
                  }}
                >
                  {a.kind === 'suspension' ? 'account suspension' : a.kind === 'group_moderation' ? 'group chat' : 'post removal'}
                </span>
                <span style={{ color: 'var(--c-fg-tertiary)' }}>
                  filed {parseServerDate(a.created_at).toLocaleString()}
                </span>
              </div>

              {/* What was done TO them — the context the decision weighs. */}
              <div className="mt-1.5" style={{ color: 'var(--c-fg-secondary)' }}>
                {a.kind === 'group_moderation' ? (
                  <>
                    Group “{a.group_title ?? 'Untitled group'}” is {a.group_state ?? 'unknown'}
                    {a.group_moderation_note ? `. Staff note: “${a.group_moderation_note}”` : '. No staff note on file'}
                    <span className="block" style={{ color: 'var(--c-fg-tertiary)' }}>
                      Filed by a group admin. The report and its snapshot are in Moderation → Group chats.
                    </span>
                  </>
                ) : a.kind === 'suspension' ? (
                  <>
                    {a.is_suspended
                      ? 'Permanently suspended'
                      : a.suspended_until
                        ? `Suspended until ${parseServerDate(a.suspended_until).toLocaleString()}`
                        : 'No longer suspended (already lifted?)'}
                    {a.suspension_reason ? ` — reason on file: “${a.suspension_reason}”` : ' — no reason on file'}
                  </>
                ) : (
                  <>
                    Removed{a.removed_at ? ` ${parseServerDate(a.removed_at).toLocaleString()}` : ''}
                    {a.removed_reason ? ` — “${a.removed_reason}”` : ' — no removal reason on file'}
                    {a.post_excerpt ? (
                      <span className="block" style={{ color: 'var(--c-fg-tertiary)' }}>
                        post: “{a.post_excerpt}”
                      </span>
                    ) : null}
                  </>
                )}
              </div>

              {/* The user's own words — the appeal itself. */}
              <blockquote
                className="mt-2 rounded-soft px-3 py-2"
                style={{ background: 'var(--c-bg-surface-alt)', color: 'var(--c-fg-default)' }}
              >
                {a.body}
              </blockquote>

              {a.status !== 'open' ? (
                <div className="mt-2 text-[12px]" style={{ color: 'var(--c-fg-secondary)' }}>
                  <b style={{ color: a.status === 'granted' ? '#5C7A3D' : '#9A4230' }}>{a.status}</b>
                  {' '}by {a.decided_by ?? '—'}
                  {a.decided_at ? ` · ${parseServerDate(a.decided_at).toLocaleString()}` : ''}
                  {a.decision_note ? ` — “${a.decision_note}”` : ''}
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={notes[a.id] ?? ''}
                    onChange={(e) => setNotes((n) => ({ ...n, [a.id]: e.target.value }))}
                    placeholder="Decision note — shown to the user"
                    className="min-w-[260px] flex-1 rounded-soft px-2.5 py-1.5 text-[12px]"
                    style={{
                      border: '1px solid var(--c-border-hairline)',
                      background: 'var(--c-bg-page)',
                      color: 'var(--c-fg-default)',
                    }}
                  />
                  <button
                    type="button"
                    disabled={busy === a.id}
                    onClick={() => decide(a.id, 'grant')}
                    className="rounded-full px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50"
                    style={{
                      border: '1px solid var(--c-border-strong)',
                      background: arming?.id === a.id && arming.action === 'grant' ? '#5C7A3D' : 'var(--c-bg-surface)',
                      color: arming?.id === a.id && arming.action === 'grant' ? '#FAF7F2' : 'var(--c-fg-default)',
                    }}
                  >
                    {arming?.id === a.id && arming.action === 'grant'
                      ? a.kind === 'suspension'
                        ? 'Click again to unsuspend'
                        : a.kind === 'group_moderation'
                          ? 'Click again to lift the group'
                          : 'Click again to restore the post'
                      : 'Grant — reverse it'}
                  </button>
                  <button
                    type="button"
                    disabled={busy === a.id}
                    onClick={() => decide(a.id, 'deny')}
                    className="rounded-full px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50"
                    style={{
                      border: '1px solid var(--c-border-strong)',
                      background: arming?.id === a.id && arming.action === 'deny' ? '#9A4230' : 'var(--c-bg-surface)',
                      color: arming?.id === a.id && arming.action === 'deny' ? '#FAF7F2' : 'var(--c-fg-default)',
                    }}
                  >
                    {arming?.id === a.id && arming.action === 'deny' ? 'Click again to deny' : 'Deny — it stands'}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  );
}
